import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Space, Typography, Tag, Statistic, Table, message, Spin } from 'antd';
import { PieChartOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, BarChart, Bar } from 'recharts';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const COLORS = ['#ff4d4f', '#faad14', '#1890ff', '#722ed1', '#eb2f96', '#52c41a', '#13c2c2', '#fa8c16', '#a0d911'];

const MOTIVO_LABEL = {
  nunca_pagou: 'Nunca pagou',
  pagou_uma_vez_cancelou: 'Pagou 1x e cancelou',
  insatisfacao_cobranca: 'Insatisfação cobrança',
  insatisfacao_convocacao: 'Insatisfação convocação',
  reclamacoes_grupo: 'Reclamações no grupo',
  muitos_acionamentos: 'Muitos acionamentos suporte',
  dificuldade_tecnica: 'Dificuldade técnica',
  sem_historico: 'Sem histórico Chatwoot',
  outros: 'Outros',
};

const ChurnAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterMotivo, setFilterMotivo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/churn-analytics');
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const motivosData = (data?.distribuicao_motivos || []).map(x => ({
    name: MOTIVO_LABEL[x.motivo] || x.motivo, value: x.count, motivo: x.motivo,
  }));

  const churnColumns = [
    { title: 'Time', dataIndex: 'game_name', width: 200,
      render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a> },
    { title: 'Status', dataIndex: 'status', width: 130,
      render: v => <Tag color={v === 'CANCELADO' ? 'red' : 'orange'}>{v}</Tag> },
    { title: 'Plano', dataIndex: 'plan_name', width: 130 },
    { title: 'Pagamentos', dataIndex: 'paid_invoices', width: 100, align: 'center',
      sorter: (a, b) => a.paid_invoices - b.paid_invoices },
    { title: 'Conv. suporte', dataIndex: 'signal_conversations', width: 100, align: 'center' },
    { title: 'Dias até churn', dataIndex: 'days_to_churn', width: 110, align: 'center',
      sorter: (a, b) => (a.days_to_churn || 0) - (b.days_to_churn || 0) },
    { title: 'Cancelou em', dataIndex: 'cancelled_at', width: 130,
      render: v => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
    { title: 'Motivos', dataIndex: 'motivos',
      render: (arr) => (
        <Space wrap size={4}>
          {(arr || []).map((m, i) => <Tag key={m} color={COLORS[i % COLORS.length]}>{MOTIVO_LABEL[m] || m}</Tag>)}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ margin: 0 }}><PieChartOutlined /> Churn Analytics</Title>
      <Text type="secondary">Análise dos {data?.total_churns || 0} times CANCELADO/PLANO_EXPIRADO. Motivos classificados por regras heurísticas cruzando conversas Chatwoot + histórico de pagamento.</Text>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>}

      {data && (
        <>
          <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
            <Col xs={24} md={5}><Card><Statistic title="Total churns" value={data.total_churns} /></Card></Col>
            <Col xs={24} md={5}>
              <Card style={{ background: '#fffbe6', borderColor: '#faad14' }}>
                <Statistic title={<span>⚠️ Nunca pagaram</span>} value={data.never_paid?.count || 0}
                  suffix={<Text type="secondary" style={{ fontSize: 12 }}>de {data.total_churns}</Text>}
                  valueStyle={{ color: '#faad14' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>Fora do donut/timeline abaixo</Text>
              </Card>
            </Col>
            <Col xs={24} md={4}><Card><Statistic title="Analisados" value={data.analyzed_count || 0} /></Card></Col>
            <Col xs={24} md={4}><Card><Statistic title="Média dias" value={data.tempo_ate_churn?.mean || 0} suffix="d" /></Card></Col>
            <Col xs={24} md={3}><Card><Statistic title="Mediana" value={data.tempo_ate_churn?.median || 0} suffix="d" /></Card></Col>
            <Col xs={24} md={3}><Card><Statistic title="P90" value={data.tempo_ate_churn?.p90 || 0} suffix="d" /></Card></Col>
          </Row>

          {data.never_paid?.count > 0 && (
            <Card title={`⚠️ ${data.never_paid.count} times nunca pagaram nenhuma mensalidade`}
              size="small" style={{ marginBottom: 16, background: '#fffbe6' }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Cadastraram, expiraram (ou cancelaram) sem gerar receita. Foco pra remarketing/oferta de conversão.
              </Text>
              <Table
                rowKey="customer_id"
                dataSource={data.never_paid.teams}
                pagination={{ pageSize: 10 }}
                size="small"
                columns={[
                  { title: 'Time', dataIndex: 'game_name',
                    render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a> },
                  { title: 'Plano', dataIndex: 'plan_name', width: 130 },
                  { title: 'Status', dataIndex: 'status', width: 140,
                    render: v => <Tag color={v === 'CANCELADO' ? 'red' : 'orange'}>{v}</Tag> },
                  { title: 'Dias no sistema', dataIndex: 'days_to_churn', width: 120, align: 'center',
                    sorter: (a, b) => (a.days_to_churn || 0) - (b.days_to_churn || 0) },
                  { title: 'Cadastrou/expirou', dataIndex: 'cancelled_at', width: 130,
                    render: v => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
                ]}
              />
            </Card>
          )}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Card title={<Space>Motivos de churn {filterMotivo && (
                <Tag closable color="blue" onClose={() => setFilterMotivo(null)}>filtro: {MOTIVO_LABEL[filterMotivo] || filterMotivo}</Tag>
              )}</Space>} extra={<Text type="secondary" style={{ fontSize: 11 }}>Clique numa fatia pra filtrar tabela</Text>}>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={motivosData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      onClick={(entry) => setFilterMotivo(prev => prev === entry.motivo ? null : entry.motivo)}
                      style={{ cursor: 'pointer' }}
                    >
                      {motivosData.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]}
                          stroke={filterMotivo === entry.motivo ? '#000' : undefined}
                          strokeWidth={filterMotivo === entry.motivo ? 3 : 1} />
                      ))}
                    </Pie>
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Timeline (churns por mês)">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={data.timeline || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" name="Churns" stroke="#ff4d4f" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24}>
              <Card title="Por plano">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.por_plano || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="plan_name" />
                    <YAxis yAxisId="left" allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Churns" fill="#ff4d4f" />
                    <Bar yAxisId="right" dataKey="avg_days" name="Média dias até churn" fill="#1890ff" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Card title={
            <Space>
              Lista completa
              {filterMotivo && (
                <Tag closable color="blue" onClose={() => setFilterMotivo(null)}>
                  filtro: {MOTIVO_LABEL[filterMotivo] || filterMotivo}
                </Tag>
              )}
              <Text type="secondary">
                ({(filterMotivo ? (data.churns || []).filter(r => r.motivos.includes(filterMotivo)) : (data.churns || [])).length} de {data.churns?.length || 0})
              </Text>
            </Space>
          }>
            <Table
              rowKey="customer_id"
              dataSource={filterMotivo ? (data.churns || []).filter(r => r.motivos.includes(filterMotivo)) : (data.churns || [])}
              columns={churnColumns}
              pagination={{ pageSize: 25 }}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default ChurnAnalytics;
